import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { PolyIcon } from "../PolyIcon";
import { Animation } from "../Animation";
import fileUploadAnimation from "assets/animations/File-Upload.json";
import { documentPreventDragDropEvents } from "../../utilities/fileUtils";

export interface FileUploadResponse {
  name: string;
  content: string;
  size: number;
  mimeType?: string;
  error?: string;
}

interface FileUploadProps {
  onUpload: (arg: FileUploadResponse) => void;
  readyText: string;
  draggingText: string;
  errorText: string;
  returnAs?: "text" | "base64";
  mimeTypes?: Array<string>;
}

/**
 * Presents a drag-and-drop file upload area (using react-dropzone)
 * and then processes the contents of the file and returns the contents.
 *
 * Currently only set up to handle a single file.
 * If refactored to support multiple files, ensure changes work
 * with RelayDeviceCertificate form.
 */
export const FileUploadReader: React.FC<FileUploadProps> = ({
  onUpload,
  readyText,
  draggingText,
  errorText,
  returnAs = "text",
  mimeTypes = []
}) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections
  } = useDropzone({
    accept: mimeTypes.join(", "),
    onDropAccepted: files => {
      handleUpload(files);
    },
    onDropRejected: errors => {
      handleErrors(errors);
    }
  });
  const [selectedFile, setSelectedFile] = useState("");
  const [hasError, setError] = useState(false);

  const resetFiles = useCallback(
    (error?: string) => {
      onUpload({ content: "", name: "", size: 0, error: error || "" });
      setSelectedFile("");
      setError(!!error);
    },
    [onUpload, setSelectedFile]
  );

  useEffect(documentPreventDragDropEvents, []);

  const handleUpload = useCallback(
    acceptedFiles => {
      if (!acceptedFiles.length) {
        return;
      }

      for (let i = 0; i <= acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        if (!file || !file.type) {
          continue;
        }

        const fileReader = new FileReader();
        fileReader.onerror = (readerEvent: ProgressEvent<FileReader>) => {
          console.error(
            "File reader event error",
            readerEvent.type,
            readerEvent?.target?.error
          );
        };

        fileReader.onabort = (readerEvent: ProgressEvent<FileReader>) => {
          console.error(
            "File reader event aborted",
            readerEvent.type,
            readerEvent?.target?.error
          );
        };

        fileReader.onload = (readerEvent: ProgressEvent<FileReader>) => {
          let content: string = (readerEvent?.target?.result || "") as string;
          content = content.replace(/^data:.+?\/.+?base64,/i, "");
          if ("text" === returnAs) {
            content = window.atob(content);
          }

          onUpload({ content, name: file.name, size: file.size });
          setSelectedFile(file.name);
          setError(false);
        };

        fileReader.readAsDataURL(file);
      }
    },
    [onUpload, returnAs]
  );

  const handleErrors = useCallback(
    fileRejections => {
      if (!fileRejections.length) {
        return;
      }

      resetFiles(fileRejections[0].errors[0].code);
    },
    [resetFiles]
  );

  const classes = [
    "dropzone",
    isDragActive && "dropzone-active",
    fileRejections.length && "has-error"
  ]
    .filter(c => !!c)
    .join(" ");

  if (selectedFile) {
    return (
      <div
        className="d-flex mb-5 nu-2 align-center"
        data-testid="file-uploader-selected-file"
      >
        <span className="d-inline-block">{selectedFile}</span>
        <button
          className="btn btn-icon"
          onClick={() => {
            resetFiles();
          }}
        >
          <PolyIcon icon="trash" className="clickable ml-5 sm" />
        </button>
      </div>
    );
  }

  return (
    <>
      {hasError && <div className="error-message d-block">{errorText}</div>}
      <div
        data-testid="file-upload-dropzone"
        className={classes}
        {...getRootProps()}
        style={{
          ...getRootProps().style
        }}
      >
        <input
          {...getInputProps()}
          data-testid="file-input"
          style={{
            ...getInputProps().style
          }}
        />
        <div className="drag-area">
          {isDragActive && (
            <>
              <Animation
                animation={{
                  light: fileUploadAnimation,
                  dark: fileUploadAnimation
                }}
                className="upload-icon-animated"
                loop={false}
              />
              <span className="drag-text">{draggingText}</span>
            </>
          )}
          {!isDragActive && (
            <>
              <PolyIcon icon="cloud_upload" className="upload-icon outline" />
              <div>{readyText}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
